import { styled } from "@mui/material";
import { keyframes } from "@mui/system";
import { visuallyHidden } from "@mui/utils";
import { useEffect, useRef, useState } from "react";

/**
 * StatusCycle — a one-line status that shimmers while it holds, then swaps to the next
 * state (transitions.dev "Thinking states", #28). Tunables are the `--think-*` vars on
 * `:root` in App.tsx; the two colours come from the caller (`--think-base` for the text,
 * `--think-highlight` for the band), since they depend on what the line sits on.
 *
 * Swap: the outgoing line exits up through a small blur while the incoming one rises in
 * from below, held back by --think-gap. Both flip in one commit and the gap is a CSS
 * transition-delay, not a timer: a timer starved by a busy main thread would leave the
 * line blank between the two. Every state is stacked in one grid cell, so the
 * box is always as tall as the tallest state and never resizes mid-swap; lines that
 * aren't showing wait, hidden, at the incoming rest state.
 *
 * Shimmer: the recipe sweeps a background-clip:text gradient by animating
 * background-position, which repaints the text every frame, forever. Here the band is a
 * highlight-coloured copy of the text inside a soft-edged window: the window translates
 * across while the copy counter-translates by the same amount, so the glyphs hold still
 * and only the window moves — transform-only, like LandPage's skeletonShimmer. The swap's
 * `filter: blur` is the recipe's, kept as a deliberate exception (as in DigitFlip): 2px on
 * one short line, for one --think-swap per swap.
 *
 * Layers: the showing line rests on translate3d(0,0,0), a 3D transform like the exit and
 * wait poses, so it holds a compositor layer of its own the whole time it shows and a swap
 * never has to move it onto one. The cost is one line-sized layer per StatusCycle; hidden
 * lines paint nothing. (Added while chasing an iPhone-only hard cut on the EcoBank hero
 * cards; it didn't cure that on its own. See --think-swap in App.tsx.)
 *
 * Only runs while it can be seen: paused (by the caller), scrolled out of view or on a
 * hidden tab, the cycle stops and the shimmer freezes. Reduced motion: no shimmer and
 * swaps are instant cuts — the states are content, so they still rotate.
 *
 * The visual lines are aria-hidden; screen readers get every state once, as one static
 * sentence, instead of a live region re-announcing every few seconds.
 */

const bandSweep = keyframes`
  from { transform: translate3d(-150%, 0, 0); }
  to   { transform: translate3d(150%, 0, 0); }
`;

// Counter-motion for the highlight copy, so its glyphs stay over the base text.
const bandHold = keyframes`
  from { transform: translate3d(150%, 0, 0); }
  to   { transform: translate3d(-150%, 0, 0); }
`;

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Reads a `--think-*` duration off :root so the JS timing stays in sync with the CSS.
const cssMs = (name: string, fallback: number) => {
  const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name));
  return Number.isFinite(v) ? v : fallback;
};

const Root = styled("span")`
  display: grid;

  /* justify-self: start hugs each line to its own text, so the band's pass is sized to
     the words it crosses rather than to the full row. */
  .t-think-line {
    grid-area: 1 / 1;
    justify-self: start;
    position: relative;
    color: var(--think-base, currentColor);
    /* A 3D rest pose, so the showing line keeps its own layer between swaps (see
       Layers, above). */
    transform: translate3d(0, 0, 0);
    transition: transform var(--think-swap) var(--think-ease), opacity var(--think-swap) var(--think-ease),
      filter var(--think-swap) var(--think-ease);
    /* Entering (wait → live) is held back by the gap… */
    transition-delay: var(--think-gap);
  }
  .t-think-line[data-state="exit"] {
    /* …exiting starts at once. */
    transition-delay: 0s;
    transform: translate3d(0, calc(var(--think-distance) * -1), 0);
    filter: blur(var(--think-blur));
    opacity: 0;
  }
  /* Resting incoming state. No transition into it: a line lands here instantly once it
     has exited, and waits hidden until its turn. */
  .t-think-line[data-state="wait"] {
    transition: none;
    transform: translate3d(0, var(--think-distance), 0);
    filter: blur(var(--think-blur));
    opacity: 0;
    visibility: hidden;
  }

  /* Band = the middle 80% of the window, soft on both sides; it travels from a full
     line-width off the left edge to a full line-width off the right, so it is off the
     text for part of each pass — the recipe's rhythm. */
  .t-think-band {
    position: absolute;
    inset: 0;
    overflow: hidden;
    pointer-events: none;
    -webkit-mask-image: linear-gradient(90deg, transparent 10%, #000 50%, transparent 90%);
    mask-image: linear-gradient(90deg, transparent 10%, #000 50%, transparent 90%);
    animation: ${bandSweep} var(--think-shimmer) linear infinite;
  }
  .t-think-band-text {
    display: block;
    color: var(--think-highlight, currentColor);
    animation: ${bandHold} var(--think-shimmer) linear infinite;
  }
  &[data-paused] .t-think-band,
  &[data-paused] .t-think-band-text {
    animation-play-state: paused;
  }

  @media (prefers-reduced-motion: reduce) {
    .t-think-line {
      transition: none;
    }
    .t-think-band {
      display: none;
    }
  }
`;

type View = { current: number; previous: number | null };

export type StatusCycleProps = {
  /** The states to rotate through, in order. One state renders as plain text. */
  states: string[];
  /** Hold the current state and freeze the shimmer (e.g. while the line is hidden). */
  paused?: boolean;
  className?: string;
};

const StatusCycle = ({ states, paused = false, className }: StatusCycleProps) => {
  const rootRef = useRef<HTMLSpanElement>(null);
  const [view, setView] = useState<View>({ current: 0, previous: null });
  const [inView, setInView] = useState(true);
  const [tabHidden, setTabHidden] = useState(() => typeof document !== "undefined" && document.hidden);
  const count = states.length;
  const cycles = count > 1;
  const running = cycles && !paused && inView && !tabHidden;

  useEffect(() => {
    const el = rootRef.current;
    if (!cycles || !el || !("IntersectionObserver" in window)) {
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting));
    observer.observe(el);
    return () => observer.disconnect();
  }, [cycles]);

  useEffect(() => {
    const onVisibility = () => setTabHidden(document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  // Hold → swap → hold … Each swap flips the outgoing line to exit and the incoming one
  // to live (the CSS holds it back by --think-gap); once both have run the outgoing line
  // drops back to wait. Stopping mid-swap settles straight onto the incoming state.
  useEffect(() => {
    if (!running) {
      return undefined;
    }
    const timers = new Set<number>();
    const later = (fn: () => void, ms: number) => {
      const id = window.setTimeout(() => {
        timers.delete(id);
        fn();
      }, ms);
      timers.add(id);
    };
    const reduce = prefersReducedMotion();
    const hold = cssMs("--think-hold", 8000);
    const swap = reduce ? 0 : cssMs("--think-swap", 250);
    const gap = reduce ? 0 : cssMs("--think-gap", 80);

    const cycle = () =>
      later(() => {
        setView((v) => ({ current: (v.current + 1) % count, previous: reduce ? null : v.current % count }));
        later(() => {
          setView((v) => ({ ...v, previous: null }));
          cycle();
        }, swap + gap);
      }, hold);
    cycle();

    return () => {
      timers.forEach((id) => window.clearTimeout(id));
      setView((v) => ({ ...v, previous: null }));
    };
  }, [running, count]);

  if (count === 0) {
    return null;
  }

  // The state list can shrink under a stored index (the caller's data changed).
  const current = view.current % count;
  const previous = view.previous === null ? null : view.previous % count;

  return (
    <>
      <Root ref={rootRef} className={className} aria-hidden="true" data-paused={running ? undefined : ""}>
        {states.map((text, i) => {
          const state = i === previous ? "exit" : i === current ? "live" : "wait";
          // Only the lines on screen carry a band; mounting it with the incoming line
          // starts each state's shimmer from the left.
          const band = cycles && (i === current || i === previous);
          return (
            <span key={i} className="t-think-line" data-state={state}>
              {text}
              {band && (
                <span className="t-think-band">
                  <span className="t-think-band-text">{text}</span>
                </span>
              )}
            </span>
          );
        })}
      </Root>
      <span style={visuallyHidden}>{states.join(". ")}</span>
    </>
  );
};

export default StatusCycle;
